import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  FileText,
  Receipt,
  Wallet
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { TERM_OPTIONS } from "../../constants/assessments";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";
import "./StudentFees.css";
import { useActiveLearner } from "../../hooks/useActiveLearner";
import { getStudentAuthHeaders } from "../../utils/studentSession";

interface StudentFee {
  id: number;
  academicYear: number;
  term: string;
  totalAmount: number;
  balance: number;
  status: string;
  className?: string;
}

interface StudentFeeItem {
  id: number;
  studentFeeId: number;
  itemName: string;
  amount: number;
  quantity: number;
  total: number;
}

interface FeePayment {
  id: number;
  studentFeeId: number;
  amount: number;
  paymentMethod?: string;
  receiptNumber?: string;
  paidAt?: string;
}

type FeeStatusKey = "paid" | "partial" | "unpaid" | "overdue";

interface FeeViewModel extends StudentFee {
  totalAmount: number;
  balance: number;
  paidAmount: number;
  progressPercent: number;
  statusKey: FeeStatusKey;
  statusLabel: string;
  invoiceTitle: string;
  itemCount: number;
  payments: FeePayment[];
  items: StudentFeeItem[];
}

interface TermSummary {
  id: string;
  term: string;
  academicYear: number;
  totalBilled: number;
  totalPaid: number;
  totalBalance: number;
  invoiceCount: number;
  statusKey: FeeStatusKey;
}

const StudentFees: React.FC = () => {
  const token = localStorage.getItem("token");
  const { activeLearner, activeLearnerId } = useActiveLearner();
  const studentId = activeLearnerId ? String(activeLearnerId) : "";

  const [fees, setFees] = useState<StudentFee[]>([]);
  const [items, setItems] = useState<StudentFeeItem[]>([]);
  const [payments, setPayments] = useState<FeePayment[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [termFilter, setTermFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const [expandedFeeId, setExpandedFeeId] = useState<number | null>(null);

  useAutoDismiss(message, setMessage, null, 5000);

  const formatCurrency = (value: number | undefined) =>
    new Intl.NumberFormat("en-ZM", {
      style: "currency",
      currency: "ZMW",
      maximumFractionDigits: 0
    }).format(value || 0);

  const formatDate = (value?: string) => {
    if (!value) return "Not recorded";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not recorded";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getStatusMeta = (fee: StudentFee): { key: FeeStatusKey; label: string } => {
    const totalAmount = Number(fee.totalAmount || 0);
    const balance = Math.max(Number(fee.balance || 0), 0);
    const paidAmount = Math.max(totalAmount - balance, 0);
    const rawStatus = String(fee.status || "").trim().toLowerCase();

    if (rawStatus === "overdue") {
      return { key: "overdue", label: "Overdue" };
    }
    if (totalAmount > 0 && balance <= 0) {
      return { key: "paid", label: "Paid" };
    }
    if (paidAmount > 0) {
      return { key: "partial", label: "Partial" };
    }
    return { key: "unpaid", label: rawStatus === "pending" ? "Pending" : "Unpaid" };
  };

  const fetchFees = useCallback(async () => {
    if (!token || !studentId) return;
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl(`fees/student/${studentId}`), {
        headers: getStudentAuthHeaders(token)
      });
      setFees(res.data?.fees || []);
      setItems(res.data?.items || []);
      setPayments(res.data?.payments || []);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to load fees.");
    } finally {
      setLoading(false);
    }
  }, [token, studentId]);

  useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  const itemsByFeeId = useMemo(() => {
    const map: Record<string, StudentFeeItem[]> = {};
    items.forEach((item) => {
      const key = String(item.studentFeeId);
      if (!map[key]) map[key] = [];
      map[key].push(item);
    });
    return map;
  }, [items]);

  const paymentsByFeeId = useMemo(() => {
    const map: Record<string, FeePayment[]> = {};
    payments.forEach((payment) => {
      const key = String(payment.studentFeeId);
      if (!map[key]) map[key] = [];
      map[key].push(payment);
    });
    return map;
  }, [payments]);

  const feeRecords = useMemo<FeeViewModel[]>(() => {
    return fees.map((fee) => {
      const totalAmount = Number(fee.totalAmount || 0);
      const balance = Math.max(Number(fee.balance || 0), 0);
      const feeItems = itemsByFeeId[String(fee.id)] || [];
      const feePayments = (paymentsByFeeId[String(fee.id)] || []).slice().sort((a, b) => {
        const aTime = a.paidAt ? new Date(a.paidAt).getTime() : 0;
        const bTime = b.paidAt ? new Date(b.paidAt).getTime() : 0;
        return bTime - aTime;
      });
      const paidAmount = Math.max(totalAmount - balance, 0);
      const status = getStatusMeta(fee);
      const invoiceTitle =
        feeItems.length === 1
          ? feeItems[0].itemName
          : feeItems.length > 1
            ? `${fee.term} Fee Invoice`
            : `${fee.term} Billing`;

      return {
        ...fee,
        totalAmount,
        balance,
        paidAmount,
        progressPercent: totalAmount > 0 ? Math.min((paidAmount / totalAmount) * 100, 100) : 0,
        statusKey: status.key,
        statusLabel: status.label,
        invoiceTitle,
        itemCount: feeItems.length,
        payments: feePayments,
        items: feeItems
      };
    });
  }, [fees, itemsByFeeId, paymentsByFeeId]);

  const totals = useMemo(() => {
    return feeRecords.reduce(
      (acc, fee) => {
        acc.totalBilled += fee.totalAmount;
        acc.totalPaid += fee.paidAmount;
        acc.totalOutstanding += fee.balance;
        if (fee.statusKey === "overdue") acc.totalOverdue += fee.balance;
        return acc;
      },
      { totalBilled: 0, totalPaid: 0, totalOutstanding: 0, totalOverdue: 0 }
    );
  }, [feeRecords]);

  const availableYears = useMemo(() => {
    const set = new Set<number>();
    feeRecords.forEach((fee) => set.add(Number(fee.academicYear)));
    return Array.from(set).sort((a, b) => b - a);
  }, [feeRecords]);

  const availableStatuses = useMemo(() => {
    const set = new Set<string>();
    feeRecords.forEach((fee) => set.add(fee.statusKey));
    return Array.from(set);
  }, [feeRecords]);

  const filteredFees = useMemo(() => {
    return feeRecords.filter((fee) => {
      const statusMatch = statusFilter === "all" || fee.statusKey === statusFilter;
      const termMatch = termFilter === "all" || fee.term === termFilter;
      const yearMatch = yearFilter === "all" || String(fee.academicYear) === yearFilter;
      return statusMatch && termMatch && yearMatch;
    });
  }, [feeRecords, statusFilter, termFilter, yearFilter]);

  const termSummaries = useMemo<TermSummary[]>(() => {
    const grouped = new Map<string, TermSummary>();

    feeRecords.forEach((fee) => {
      const key = `${fee.academicYear}-${fee.term}`;
      const current = grouped.get(key) || {
        id: key,
        term: fee.term,
        academicYear: fee.academicYear,
        totalBilled: 0,
        totalPaid: 0,
        totalBalance: 0,
        invoiceCount: 0,
        statusKey: "unpaid" as FeeStatusKey
      };

      current.totalBilled += fee.totalAmount;
      current.totalPaid += fee.paidAmount;
      current.totalBalance += fee.balance;
      current.invoiceCount += 1;

      if (fee.statusKey === "overdue") current.statusKey = "overdue";
      else if (current.statusKey !== "overdue" && fee.statusKey === "partial") current.statusKey = "partial";
      else if (current.statusKey === "unpaid" && fee.statusKey === "paid" && current.totalBalance === 0) {
        current.statusKey = "paid";
      }

      grouped.set(key, current);
    });

    return Array.from(grouped.values()).sort((a, b) => {
      if (b.academicYear !== a.academicYear) return b.academicYear - a.academicYear;
      return TERM_OPTIONS.indexOf(a.term) - TERM_OPTIONS.indexOf(b.term);
    });
  }, [feeRecords]);

  const paymentHistory = useMemo(() => {
    return payments
      .map((payment) => ({
        ...payment,
        linkedFee: feeRecords.find((fee) => fee.id === payment.studentFeeId)
      }))
      .sort((a, b) => {
        const aTime = a.paidAt ? new Date(a.paidAt).getTime() : 0;
        const bTime = b.paidAt ? new Date(b.paidAt).getTime() : 0;
        return bTime - aTime;
      });
  }, [payments, feeRecords]);

  const expandedFee = useMemo(
    () => filteredFees.find((fee) => fee.id === expandedFeeId) || null,
    [expandedFeeId, filteredFees]
  );

  useEffect(() => {
    if (!filteredFees.some((fee) => fee.id === expandedFeeId)) {
      setExpandedFeeId(filteredFees[0]?.id ?? null);
    }
  }, [expandedFeeId, filteredFees]);

  const downloadReceipt = async (paymentId: number) => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl(`fees/receipt/${paymentId}`), {
        headers: getStudentAuthHeaders(token),
        responseType: "blob"
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `fee_receipt_${paymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setMessage("Failed to download receipt.");
    }
  };

  const renderEmptyState = (title: string, description: string, secondaryText: string) => (
    <div className="student-fees-empty-state" role="status">
      <div className="student-fees-empty-icon">
        <Receipt size={24} />
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      <span>{secondaryText}</span>
    </div>
  );

  return (
    <div className="utopia-page student-fees-page">
      <div className="student-fees-shell">
        <section className="utopia-page-container student-fees-hero">
          <div>
            <p className="student-fees-eyebrow">Student Finance</p>
            <h1 className="utopia-page-title">My Fees</h1>
            <p className="student-fees-description">
              Track your fee balances, payment history, and outstanding amounts.
            </p>
            {activeLearner && (
              <p className="student-fees-description">
                Viewing: {activeLearner.name}{activeLearner.className ? ` • ${activeLearner.className}` : ""}
              </p>
            )}
          </div>
          <div className="student-fees-hero-badge">
            <Wallet size={18} />
            <span>Read-only student billing view</span>
          </div>
        </section>

        {message && <div className="utopia-form-error">{message}</div>}

        {loading ? (
          <div className="student-fees-loading utopia-page-container">Loading fee records...</div>
        ) : (
          <>
            <section className="student-fees-summary-grid">
              <article className="student-fees-summary-card billed">
                <div className="student-fees-summary-icon">
                  <FileText size={18} />
                </div>
                <div>
                  <p>Total Billed</p>
                  <strong>{formatCurrency(totals.totalBilled)}</strong>
                  <span>All assigned invoices</span>
                </div>
              </article>
              <article className="student-fees-summary-card paid">
                <div className="student-fees-summary-icon">
                  <CheckCircle2 size={18} />
                </div>
                <div>
                  <p>Total Paid</p>
                  <strong>{formatCurrency(totals.totalPaid)}</strong>
                  <span>Confirmed payments posted</span>
                </div>
              </article>
              <article className="student-fees-summary-card outstanding">
                <div className="student-fees-summary-icon">
                  <Wallet size={18} />
                </div>
                <div>
                  <p>Outstanding Balance</p>
                  <strong>{formatCurrency(totals.totalOutstanding)}</strong>
                  <span>Remaining amount still due</span>
                </div>
              </article>
              <article className="student-fees-summary-card overdue">
                <div className="student-fees-summary-icon">
                  <AlertTriangle size={18} />
                </div>
                <div>
                  <p>Overdue Amount</p>
                  <strong>{formatCurrency(totals.totalOverdue)}</strong>
                  <span>Only invoices marked overdue</span>
                </div>
              </article>
            </section>

            <section className="utopia-page-container student-fees-section">
              <div className="student-fees-section-heading">
                <div>
                  <h2>Billing by Term</h2>
                  <p>Review how each school term contributes to your overall balance.</p>
                </div>
              </div>

              {termSummaries.length === 0 ? (
                renderEmptyState(
                  "No fees are currently assigned to your account.",
                  "Once the school billing office assigns fees, they will appear here.",
                  "You can still return here later to review balances and receipts."
                )
              ) : (
                <div className="student-fees-term-grid">
                  {termSummaries.map((termSummary) => (
                    <article key={termSummary.id} className="student-fees-term-card">
                      <div className="student-fees-term-card-top">
                        <div>
                          <h3>
                            {termSummary.term} {termSummary.academicYear}
                          </h3>
                          <span>{termSummary.invoiceCount} invoice{termSummary.invoiceCount === 1 ? "" : "s"}</span>
                        </div>
                        <span className={`status-pill status-${termSummary.statusKey}`}>
                          {termSummary.statusKey}
                        </span>
                      </div>
                      <dl className="student-fees-term-stats">
                        <div>
                          <dt>Billed</dt>
                          <dd>{formatCurrency(termSummary.totalBilled)}</dd>
                        </div>
                        <div>
                          <dt>Paid</dt>
                          <dd>{formatCurrency(termSummary.totalPaid)}</dd>
                        </div>
                        <div>
                          <dt>Balance</dt>
                          <dd>{formatCurrency(termSummary.totalBalance)}</dd>
                        </div>
                      </dl>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="utopia-page-container student-fees-section">
              <div className="student-fees-section-heading">
                <div>
                  <h2>Assigned Fees</h2>
                  <p>View invoice totals, payment progress, and the status of each fee record.</p>
                </div>
              </div>

              {feeRecords.length > 0 && (
                <div className="student-fees-filters">
                  <div>
                    <label className="student-fees-filter-label">Status</label>
                    <select
                      className="utopia-input"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Statuses</option>
                      {availableStatuses.map((status) => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="student-fees-filter-label">Term</label>
                    <select
                      className="utopia-input"
                      value={termFilter}
                      onChange={(e) => setTermFilter(e.target.value)}
                    >
                      <option value="all">All Terms</option>
                      {TERM_OPTIONS.map((term) => (
                        <option key={term} value={term}>
                          {term}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="student-fees-filter-label">Year</label>
                    <select
                      className="utopia-input"
                      value={yearFilter}
                      onChange={(e) => setYearFilter(e.target.value)}
                    >
                      <option value="all">All Years</option>
                      {availableYears.map((year) => (
                        <option key={year} value={String(year)}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {feeRecords.length === 0 ? (
                renderEmptyState(
                  "No fees are currently assigned to your account.",
                  "Once the school billing office assigns fees, they will appear here.",
                  "This section will show billed amounts, balances, and invoice details."
                )
              ) : filteredFees.length === 0 ? (
                <div className="student-fees-empty-panel">No fee records match the selected filters.</div>
              ) : (
                <>
                  <div className="student-fees-table-wrap">
                    <table className="utopia-table student-fees-table">
                      <thead>
                        <tr>
                          <th>Term</th>
                          <th>Fee Item / Invoice</th>
                          <th>Amount Billed</th>
                          <th>Amount Paid</th>
                          <th>Balance</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFees.map((fee) => (
                          <tr key={fee.id}>
                            <td>
                              <div className="utopia-info-cell">
                                <span className="utopia-table-cell-primary">{fee.term}</span>
                                <span className="utopia-table-cell-secondary">{fee.academicYear}</span>
                              </div>
                            </td>
                            <td>
                              <div className="utopia-info-cell">
                                <span className="utopia-table-cell-primary">{fee.invoiceTitle}</span>
                                <span className="utopia-table-cell-secondary">
                                  {fee.className || "Student account"} • {fee.itemCount} item{fee.itemCount === 1 ? "" : "s"}
                                </span>
                              </div>
                            </td>
                            <td>{formatCurrency(fee.totalAmount)}</td>
                            <td>{formatCurrency(fee.paidAmount)}</td>
                            <td>{formatCurrency(fee.balance)}</td>
                            <td>
                              <span className={`status-pill status-${fee.statusKey}`}>{fee.statusLabel}</span>
                            </td>
                            <td>
                              <div className="student-fees-row-actions">
                                <button
                                  type="button"
                                  className="utopia-btn-secondary"
                                  onClick={() => setExpandedFeeId((current) => (current === fee.id ? null : fee.id))}
                                >
                                  {expandedFeeId === fee.id ? "Hide details" : "View details"}
                                </button>
                                {fee.payments[0]?.id ? (
                                  <button
                                    type="button"
                                    className="utopia-btn-secondary"
                                    onClick={() => downloadReceipt(fee.payments[0].id)}
                                  >
                                    Latest receipt
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="student-fees-mobile-list">
                    {filteredFees.map((fee) => (
                      <article key={`mobile-${fee.id}`} className="student-fees-mobile-card">
                        <div className="student-fees-mobile-card-head">
                          <div>
                            <h3>{fee.invoiceTitle}</h3>
                            <p>
                              {fee.term} {fee.academicYear}
                            </p>
                          </div>
                          <span className={`status-pill status-${fee.statusKey}`}>{fee.statusLabel}</span>
                        </div>

                        <dl className="student-fees-mobile-stats">
                          <div>
                            <dt>Billed</dt>
                            <dd>{formatCurrency(fee.totalAmount)}</dd>
                          </div>
                          <div>
                            <dt>Paid</dt>
                            <dd>{formatCurrency(fee.paidAmount)}</dd>
                          </div>
                          <div>
                            <dt>Balance</dt>
                            <dd>{formatCurrency(fee.balance)}</dd>
                          </div>
                        </dl>

                        <div className="student-fee-progress">
                          <div className="student-fee-progress-bar">
                            <span style={{ width: `${fee.progressPercent}%` }} />
                          </div>
                          <div className="student-fee-progress-meta">
                            <span>{fee.progressPercent.toFixed(0)}% paid</span>
                            <span>{fee.itemCount} billed item{fee.itemCount === 1 ? "" : "s"}</span>
                          </div>
                        </div>

                        <div className="student-fees-row-actions">
                          <button
                            type="button"
                            className="utopia-btn-secondary"
                            onClick={() => setExpandedFeeId((current) => (current === fee.id ? null : fee.id))}
                          >
                            {expandedFeeId === fee.id ? "Hide details" : "View details"}
                          </button>
                          {fee.payments[0]?.id ? (
                            <button
                              type="button"
                              className="utopia-btn-secondary"
                              onClick={() => downloadReceipt(fee.payments[0].id)}
                            >
                              Latest receipt
                            </button>
                          ) : null}
                        </div>
                      </article>
                    ))}
                  </div>

                  {expandedFee && (
                    <article className="student-fees-detail-card">
                      <div className="student-fees-detail-header">
                        <div>
                          <h3>{expandedFee.invoiceTitle}</h3>
                          <p>
                            {expandedFee.term} {expandedFee.academicYear} billing details
                          </p>
                        </div>
                        <button
                          type="button"
                          className="student-fees-inline-button"
                          onClick={() => setExpandedFeeId(null)}
                        >
                          Collapse
                        </button>
                      </div>

                      <div className="student-fees-detail-metrics">
                        <div>
                          <span>Amount billed</span>
                          <strong>{formatCurrency(expandedFee.totalAmount)}</strong>
                        </div>
                        <div>
                          <span>Amount paid</span>
                          <strong>{formatCurrency(expandedFee.paidAmount)}</strong>
                        </div>
                        <div>
                          <span>Outstanding balance</span>
                          <strong>{formatCurrency(expandedFee.balance)}</strong>
                        </div>
                        <div>
                          <span>Status</span>
                          <strong>{expandedFee.statusLabel}</strong>
                        </div>
                      </div>

                      <div className="student-fees-detail-columns">
                        <div className="student-fees-detail-panel">
                          <div className="student-fees-panel-title">
                            <FileText size={16} />
                            <h4>Invoice Breakdown</h4>
                          </div>
                          {expandedFee.items.length === 0 ? (
                            <p className="student-fees-muted-copy">No fee line items were attached to this invoice.</p>
                          ) : (
                            <div className="student-fees-mini-table">
                              {expandedFee.items.map((item) => (
                                <div key={item.id} className="student-fees-mini-row">
                                  <div>
                                    <strong>{item.itemName}</strong>
                                    <span>
                                      {item.quantity} × {formatCurrency(item.amount)}
                                    </span>
                                  </div>
                                  <span>{formatCurrency(item.total)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="student-fees-detail-panel">
                          <div className="student-fees-panel-title">
                            <CreditCard size={16} />
                            <h4>Linked Payments</h4>
                          </div>
                          {expandedFee.payments.length === 0 ? (
                            <p className="student-fees-muted-copy">
                              No payments have been posted against this invoice yet.
                            </p>
                          ) : (
                            <div className="student-fees-mini-table">
                              {expandedFee.payments.map((payment) => (
                                <div key={payment.id} className="student-fees-mini-row">
                                  <div>
                                    <strong>{formatCurrency(payment.amount)}</strong>
                                    <span>
                                      {payment.paymentMethod || "Payment"} • {formatDate(payment.paidAt)}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    className="student-fees-inline-button"
                                    onClick={() => downloadReceipt(payment.id)}
                                  >
                                    Receipt
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  )}
                </>
              )}
            </section>

            <section className="utopia-page-container student-fees-section">
              <div className="student-fees-section-heading">
                <div>
                  <h2>Payment History</h2>
                  <p>See receipts, payment methods, and the invoice linked to each payment.</p>
                </div>
              </div>

              {paymentHistory.length === 0 ? (
                renderEmptyState(
                  "No payments have been recorded yet.",
                  "When payments are posted to your account, they will appear in this history.",
                  "Receipts and payment methods will be listed here once available."
                )
              ) : (
                <>
                  <div className="student-fees-table-wrap">
                    <table className="utopia-table student-fees-table">
                      <thead>
                        <tr>
                          <th>Payment Date</th>
                          <th>Receipt Number</th>
                          <th>Linked Invoice</th>
                          <th>Method</th>
                          <th>Amount Paid</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentHistory.map((payment) => (
                          <tr key={payment.id}>
                            <td>
                              <div className="utopia-info-row">
                                <CalendarDays size={14} />
                                <span>{formatDate(payment.paidAt)}</span>
                              </div>
                            </td>
                            <td>{payment.receiptNumber || `Receipt #${payment.id}`}</td>
                            <td>
                              <div className="utopia-info-cell">
                                <span className="utopia-table-cell-primary">
                                  {payment.linkedFee?.invoiceTitle || "Fee payment"}
                                </span>
                                <span className="utopia-table-cell-secondary">
                                  {payment.linkedFee
                                    ? `${payment.linkedFee.term} ${payment.linkedFee.academicYear}`
                                    : "Invoice details unavailable"}
                                </span>
                              </div>
                            </td>
                            <td>{payment.paymentMethod || "Not recorded"}</td>
                            <td>{formatCurrency(Number(payment.amount || 0))}</td>
                            <td>
                              <span className="status-pill status-paid">Posted</span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="utopia-btn-secondary"
                                onClick={() => downloadReceipt(payment.id)}
                              >
                                <Receipt size={14} />
                                Receipt
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="student-fees-mobile-list">
                    {paymentHistory.map((payment) => (
                      <article key={`payment-${payment.id}`} className="student-fees-mobile-card">
                        <div className="student-fees-mobile-card-head">
                          <div>
                            <h3>{formatCurrency(Number(payment.amount || 0))}</h3>
                            <p>{payment.receiptNumber || `Receipt #${payment.id}`}</p>
                          </div>
                          <span className="status-pill status-paid">Posted</span>
                        </div>
                        <dl className="student-fees-mobile-stats">
                          <div>
                            <dt>Date</dt>
                            <dd>{formatDate(payment.paidAt)}</dd>
                          </div>
                          <div>
                            <dt>Method</dt>
                            <dd>{payment.paymentMethod || "Not recorded"}</dd>
                          </div>
                          <div>
                            <dt>Invoice</dt>
                            <dd>
                              {payment.linkedFee
                                ? `${payment.linkedFee.term} ${payment.linkedFee.academicYear}`
                                : "Unavailable"}
                            </dd>
                          </div>
                        </dl>
                        <button
                          type="button"
                          className="utopia-btn-secondary"
                          onClick={() => downloadReceipt(payment.id)}
                        >
                          <Receipt size={14} />
                          Receipt
                        </button>
                      </article>
                    ))}
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentFees;
